import React, { useState, useEffect, useRef } from "react";
import { getUserProfile } from "../../services/api";
import { countries } from "../../constants/types";

// List of academic rank/title options for dropdown
const RANK_TITLES = [
  "Professor",
  "Associate Professor", 
  "Assistant Professor",
  "Lecturer",
  "Senior Lecturer",
  "Instructor",
  "PhD Student",
  "Other"
];

/**
 * PaperAuthors Component
 * 
 * Step 4 of the paper submission wizard.
 * Handles author information with auto-completion from AI extraction.
 * Automatically adds the current user and AI-extracted authors.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.handleChange - Form field change handler
 * @param {Object} props.extractedInfo - AI-extracted information
 * @param {boolean} props.hasExtractedInfo - Whether AI extraction was performed
 * @param {boolean} props.submissionAttempted - Whether user has attempted to submit the form
 */
const PaperAuthors = ({
  formData,
  handleChange,
  extractedInfo,
  hasExtractedInfo,
  submissionAttempted = false
}) => {
  const [currentUserMissing, setCurrentUserMissing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUserProfile, setLoadingUserProfile] = useState(true);
  // State to track which author forms are expanded
  const [expandedAuthors, setExpandedAuthors] = useState(new Set());
  // State to prevent auto-collapse during manual button interactions
  const [manualToggleInProgress, setManualToggleInProgress] = useState(false);
  // State to track custom rank titles for authors who select "Other"
  const [customRankTitles, setCustomRankTitles] = useState({});
  
  // Ref to track if component is mounted
  const mountedRef = useRef(true);
  
  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef(new Set());

  // Ref to track the last processed extracted info to detect changes
  const lastExtractedInfoRef = useRef(null);

  // Initialize with formData authors or empty array
  const [paperAuthors, setPaperAuthors] = useState(
    formData.paperAuthors && Array.isArray(formData.paperAuthors) 
      ? formData.paperAuthors 
      : []
  );

  // Cleanup effect to handle component unmounting
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      // Clear all pending timeouts
      timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutsRef.current.clear();
    };
  }, []);

  // Reset auto-add flag when extracted information changes (new paper uploaded)
  useEffect(() => {
    // Handle case when extracted info is cleared (null)
    if (!hasExtractedInfo && !extractedInfo) {
      const lastExtractedInfoString = lastExtractedInfoRef.current;
      
      // If we previously had extracted info but now it's cleared
      if (lastExtractedInfoString !== null) {
        
        // Clear existing authors to start fresh
        setPaperAuthors([]);
        
        // Reset the auto-add flag so authors can be re-processed
        handleChange({
          target: {
            name: 'autoAddedAuthors',
            value: false
          }
        });
        
        // Clear form data authors as well
        handleChange({
          target: {
            name: 'paperAuthors',
            value: []
          }
        });
        
        // Reset current user validation flag
        handleChange({
          target: {
            name: 'currentUserInAuthors',
            value: false
          }
        });
        
        // Update the ref to track this cleared state
        lastExtractedInfoRef.current = null;
      }
      return;
    }
    
    // Check if we have new extracted info that's different from what we last processed
    if (hasExtractedInfo && extractedInfo) {
      const currentExtractedInfoString = JSON.stringify(extractedInfo);
      const lastExtractedInfoString = lastExtractedInfoRef.current;
      
      // If this is new extracted info (different from last processed)
      if (lastExtractedInfoString !== currentExtractedInfoString) {
        
        // Clear existing authors to start fresh with new extraction
        setPaperAuthors([]);
        
        // Reset the auto-add flag so authors can be re-processed
        handleChange({
          target: {
            name: 'autoAddedAuthors',
            value: false
          }
        });
        
        // Clear form data authors as well
        handleChange({
          target: {
            name: 'paperAuthors',
            value: []
          }
        });
        
        // Update the ref to track this new extracted info
        lastExtractedInfoRef.current = currentExtractedInfoString;
      }
    }
  }, [extractedInfo, hasExtractedInfo]);

  // Load current user profile on component mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        setLoadingUserProfile(true);
        const profile = await getUserProfile();
        setCurrentUser(profile);
      } catch (error) {
        console.error("Error loading current user profile:", error);
      } finally {
        setLoadingUserProfile(false);
      }
    };

    loadCurrentUser();
  }, []);

  // Safe setTimeout that checks if component is mounted
  const safeSetTimeout = (callback, delay) => {
    const timeoutId = setTimeout(() => {
      if (mountedRef.current) {
        callback();
      }
      // Remove completed timeout from tracking
      timeoutsRef.current.delete(timeoutId);
    }, delay);
    
    // Track timeout for cleanup
    timeoutsRef.current.add(timeoutId);
    return timeoutId;
  };

  // Initial check for current user presence when user profile loads
  useEffect(() => {
    if (currentUser && !loadingUserProfile) {
      const userMissing = !checkCurrentUserInAuthors(paperAuthors);
      setCurrentUserMissing(userMissing);
      
      // Update validation status in form data
      handleChange({
        target: {
          name: 'currentUserInAuthors',
          value: !userMissing
        }
      });
    }
  }, [currentUser, loadingUserProfile]);

  /**
   * Check if an author is the current user
   */
  const isCurrentUserAuthor = (author) => {
    // Check for explicit flag first
    if (author.isCurrentUser === true) {
      return true;
    }
    
    if (!currentUser) {
      return false;
    }
    
    const currentUserFirstName = (currentUser.firstName || "").toLowerCase().trim();
    const currentUserLastName = (currentUser.lastName || "").toLowerCase().trim();
    
    if (!currentUserFirstName && !currentUserLastName) {
      return false;
    }
    
    const authorFirstName = (author.firstName || "").toLowerCase().trim();
    const authorLastName = (author.lastName || "").toLowerCase().trim();
    
    return (authorFirstName === currentUserFirstName && authorLastName === currentUserLastName);
  };

  /**
   * Check if current user exists in the authors list
   */
  const checkCurrentUserInAuthors = (authors) => {
    // Check for explicit flag first
    const hasCurrentUserFlag = authors.some(author => author.isCurrentUser === true);
    if (hasCurrentUserFlag) {
      return true;
    }
    
    if (!currentUser) {
      return false;
    }
    
    const currentUserFirstName = (currentUser.firstName || "").toLowerCase().trim();
    const currentUserLastName = (currentUser.lastName || "").toLowerCase().trim();
    
    if (!currentUserFirstName && !currentUserLastName) {
      return false; // No current user info to check
    }
    
    const userExists = authors.some(author => {
      const authorFirstName = (author.firstName || "").toLowerCase().trim();
      const authorLastName = (author.lastName || "").toLowerCase().trim();
      
      return (authorFirstName === currentUserFirstName && authorLastName === currentUserLastName);
    });
    
    return userExists;
  };

  /**
   * Update form data and check current user presence
   */
  const updateFormDataAndCheckUser = (updatedAuthors) => {
    const userMissing = !checkCurrentUserInAuthors(updatedAuthors);
    setCurrentUserMissing(userMissing);
    
    // Update form data
    handleChange({
      target: {
        name: 'paperAuthors',
        value: updatedAuthors
      }
    });
    
    // Also update the validation status for submit button
    handleChange({
      target: {
        name: 'currentUserInAuthors',
        value: !userMissing
      }
    });
  };

  // Auto-add current user and AI-extracted authors
  useEffect(() => {
    // Wait for user profile to load
    if (loadingUserProfile || !currentUser) {
      return;
    }

    if (!formData.autoAddedAuthors) {
      const authorsToAdd = [];
      
      // Always add current user as first author if not already present
      const currentUserExists = paperAuthors.some(author => 
        isCurrentUserAuthor(author)
      );
      
      // Check if we have enough current user info to proceed
      const hasUserInfo = (currentUser.firstName && currentUser.firstName.trim()) || 
                         (currentUser.lastName && currentUser.lastName.trim());
      
      if (!currentUserExists && hasUserInfo) {
        const currentUserAuthor = {
          id: Date.now(),
          authorId: "",
          firstName: currentUser.firstName || "",
          lastName: currentUser.lastName || "",
          email: currentUser.email || "",
          institution: currentUser.primaryAffiliation?.institution || "",
          department: currentUser.primaryAffiliation?.department || "",
          rankTitle: currentUser.primaryAffiliation?.title || "",
          country: currentUser.primaryAffiliation?.address?.country || "",
          isCorresponding: true, // Current user is corresponding author by default
          authorOrder: 1,
          isCurrentUser: true // Flag to identify current user
        };
        
        authorsToAdd.push(currentUserAuthor);
      }
      
            // Add AI-extracted authors (matching current user if found)
      if (hasExtractedInfo && extractedInfo?.extractedInfo?.authors) {
        const aiAuthors = extractedInfo.extractedInfo.authors;
        
        aiAuthors.forEach((aiAuthor, index) => {
          const aiFirstName = (aiAuthor.Name || aiAuthor.firstName || "").toLowerCase().trim();
          const aiLastName = (aiAuthor.Surname || aiAuthor.lastName || "").toLowerCase().trim();
          const currentFirstName = (currentUser.firstName || "").toLowerCase().trim();
          const currentLastName = (currentUser.lastName || "").toLowerCase().trim();
          
          // Check if this AI author matches current user
          const isCurrentUserMatch = (aiFirstName === currentFirstName && aiLastName === currentLastName);
          
          if (isCurrentUserMatch && authorsToAdd.length > 0) {
            // Update current user info with AI data
            authorsToAdd[0] = {
              ...authorsToAdd[0],
              firstName: aiAuthor.Name || aiAuthor.firstName || authorsToAdd[0].firstName,
              lastName: aiAuthor.Surname || aiAuthor.lastName || authorsToAdd[0].lastName,
              email: aiAuthor.email || authorsToAdd[0].email,
              institution: aiAuthor.Affiliation || aiAuthor.institution || authorsToAdd[0].institution,
              department: aiAuthor.Department || aiAuthor.department || authorsToAdd[0].department,
              rankTitle: aiAuthor.Title || aiAuthor.title || authorsToAdd[0].rankTitle,
              country: aiAuthor.Country || aiAuthor.country || authorsToAdd[0].country
            };
          } else {
            // Add as separate author
            const newAIAuthor = {
              id: Date.now() + index + 1000,
              authorId: "",
              firstName: aiAuthor.Name || aiAuthor.firstName || "",
              lastName: aiAuthor.Surname || aiAuthor.lastName || "",
              email: aiAuthor.email || "",
              institution: aiAuthor.Affiliation || aiAuthor.institution || "",
              department: aiAuthor.Department || aiAuthor.department || "",
              rankTitle: aiAuthor.Title || aiAuthor.title || "",
              country: aiAuthor.Country || aiAuthor.country || "",
              isCorresponding: false, // AI authors are not corresponding by default
              authorOrder: (authorsToAdd.length || paperAuthors.length) + 1,
              isCurrentUser: false
            };
            
            // Only add if they have at least a name
            if (newAIAuthor.firstName || newAIAuthor.lastName) {
              authorsToAdd.push(newAIAuthor);
            }
          }
        });
      }
      
      if (authorsToAdd.length > 0) {
        const updatedAuthors = [...paperAuthors, ...authorsToAdd];
        // Reorder all authors
        const reorderedAuthors = updatedAuthors.map((author, i) => ({
          ...author,
          authorOrder: i + 1
        }));
        setPaperAuthors(reorderedAuthors);
        // Mark auto-add as completed in formData
        handleChange({
          target: {
            name: 'autoAddedAuthors',
            value: true
          }
        });
        // Update form data and check user presence
        updateFormDataAndCheckUser(reorderedAuthors);
        
        // Auto-expand authors with missing required information
        const authorsWithMissingInfo = new Set();
        reorderedAuthors.forEach(author => {
          // Skip current user as their info is auto-populated
          if (!isCurrentUserAuthor(author)) {
            if (!hasRequiredInfo(author)) {
              authorsWithMissingInfo.add(author.id);
            }
          }
        });
        
        setExpandedAuthors(authorsWithMissingInfo);
      } else {
        // Mark auto-add as completed in formData
        handleChange({
          target: {
            name: 'autoAddedAuthors',
            value: true
          }
        });
        
        // Even if no authors were added, we still need to check if current user is missing
        if (paperAuthors.length > 0) {
          const userMissing = !checkCurrentUserInAuthors(paperAuthors);
          setCurrentUserMissing(userMissing);
          handleChange({
            target: {
              name: 'currentUserInAuthors',
              value: !userMissing
            }
          });
        }
      }
    }
  }, [currentUser, loadingUserProfile, extractedInfo, hasExtractedInfo, formData.autoAddedAuthors]);

  // Check current user presence whenever paperAuthors changes or user loads
  useEffect(() => {
    if (currentUser && !loadingUserProfile) {
      const userMissing = !checkCurrentUserInAuthors(paperAuthors);
      
      setCurrentUserMissing(userMissing);
      
      // Always update the validation status in form data
      handleChange({
        target: {
          name: 'currentUserInAuthors',
          value: !userMissing
        }
      });
    }
  }, [paperAuthors, currentUser, loadingUserProfile]);

  // Sync paperAuthors with formData, combining custom rank titles where needed
  useEffect(() => {
    const processedAuthors = paperAuthors.map(author => {
      // If author selected "Other" for rank title, combine it with the custom title
      if (author.rankTitle === "Other" && customRankTitles[author.id]) {
        return {
          ...author,
          rankTitle: customRankTitles[author.id]
        };
      }
      return author;
    });

    handleChange({
      target: {
        name: 'paperAuthors',
        value: processedAuthors
      }
    });
  }, [paperAuthors, customRankTitles]);

  // Toggle expansion state for a specific author
  const toggleAuthorExpansion = (authorId) => {
    
    setExpandedAuthors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(authorId)) {
        newSet.delete(authorId);
      } else {
        newSet.add(authorId);
      }
      return newSet;
    });
  };

  // Smart toggle that prevents collapsing incomplete required forms
  const smartToggleAuthorExpansion = (authorId) => {
    
    // Check if component is still mounted
    if (!mountedRef.current) return;
    
    // Set manual toggle flag to prevent auto-collapse interference
    setManualToggleInProgress(true);
    
    const author = paperAuthors.find(a => a && a.id === authorId);
    if (!author) {
      if (mountedRef.current) {
        setManualToggleInProgress(false);
      }
      return;
    }

    const isCurrentUser = isCurrentUserAuthor(author);
    const isCurrentlyExpanded = expandedAuthors.has(authorId);
    
    // For current user, we can always collapse since info is auto-populated
    if (isCurrentUser) {
      toggleAuthorExpansion(authorId);
      // Clear the flag after a short delay
      safeSetTimeout(() => {
        if (mountedRef.current) {
          setManualToggleInProgress(false);
        }
      }, 200);
      return;
    }

    // For other authors, check if they have minimal required information
    const authorHasRequiredInfo = hasRequiredInfo(author);
    
    // If trying to collapse but missing required info, keep it expanded
    if (isCurrentlyExpanded && !authorHasRequiredInfo) {
      if (mountedRef.current) {
        setManualToggleInProgress(false);
      }
      return; // Don't collapse incomplete forms
    }
    
    toggleAuthorExpansion(authorId);
    // Clear the flag after a short delay
    safeSetTimeout(() => {
      if (mountedRef.current) {
        setManualToggleInProgress(false);
      }
    }, 200);
  };

  // Function to expand a newly added author
  const expandNewAuthor = (authorId) => {
    setExpandedAuthors(prev => new Set([...prev, authorId]));
  };

  /**
   * Check if a field is missing for an author (for highlighting)
   * @param {Object} author - The author object
   * @param {string} field - The field name to check
   * @returns {boolean} True if the field is missing and should be highlighted
   */
  const isFieldMissing = (author, field) => {
    // Don't highlight missing fields for current user as they're auto-populated
    if (isCurrentUserAuthor(author)) {
      return false;
    }
    
    // Check if the required field is missing
    const requiredFields = ['firstName', 'lastName', 'email', 'institution', 'country', 'rankTitle'];
    if (!requiredFields.includes(field)) {
      return false;
    }
    
    // Special validation for rankTitle field
    if (field === 'rankTitle') {
      // No rank title selected
      if (!author.rankTitle) {
        return true;
      }
      // "Other" selected but no custom title provided
      if (author.rankTitle === "Other" && (!customRankTitles[author.id] || customRankTitles[author.id].trim() === '')) {
        return true;
      }
      return false;
    }
    
    // Standard field validation
    return !author[field] || author[field].trim() === '';
  };

  /**
   * Check if an author has all required information
   * @param {Object} author - The author object
   * @returns {boolean} True if the author has all required information
   */
  const hasRequiredInfo = (author) => {
    // Basic required fields
    const basicInfo = author.firstName && author.lastName && author.email && 
                      author.institution && author.country && author.rankTitle;
    
    if (!basicInfo) {
      return false;
    }
    
    // If "Other" is selected, check for custom rank title
    if (author.rankTitle === "Other") {
      return customRankTitles[author.id] && customRankTitles[author.id].trim() !== '';
    }
    
    return true;
  };

      const extractedAuthors = Array.isArray(extractedInfo?.extractedInfo?.authors)
        ? extractedInfo.extractedInfo.authors
        : [];

      const hasBlindSubmissionWarning = hasExtractedInfo && (
        extractedAuthors.length === 0 || (
          extractedAuthors.length === 1 && (() => {
            const firstAuthor = extractedAuthors[0] || {};
            const authorName = [
              firstAuthor.Name,
              firstAuthor.firstName,
              firstAuthor.Surname,
              firstAuthor.lastName,
              firstAuthor.name
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();

            return authorName.includes('blind');
          })()
        )
      );

  /**
   * Add a new author to the paper
   */
  const addAuthor = () => {
    const newAuthor = {
      id: Date.now(),
      authorId: "",
      firstName: "",
      lastName: "",
      email: "",
      institution: "",
      department: "",
      rankTitle: "",
      country: "",
      isCorresponding: false,
      authorOrder: paperAuthors.length + 1,
      isCurrentUser: false // New authors are not current user
    };
    const updatedAuthors = [...paperAuthors, newAuthor];
    setPaperAuthors(updatedAuthors);
    // Update form data and check user presence
    updateFormDataAndCheckUser(updatedAuthors);
    // Auto-expand the newly added author
    expandNewAuthor(newAuthor.id);
  };

  /**
   * Remove an author from the paper (cannot remove current user)
   */
  const removeAuthor = (index) => {
    const authorToRemove = paperAuthors[index];
    
    // Check if this is the current user - prevent removal
    if (isCurrentUserAuthor(authorToRemove)) {
      alert("Cannot remove the current user from the authors list. The current user must always be included as an author.");
      return;
    }
    
    // Clean up custom rank title for this author
    if (customRankTitles[authorToRemove.id]) {
      setCustomRankTitles(prev => {
        const updated = { ...prev };
        delete updated[authorToRemove.id];
        return updated;
      });
    }
    
    const updatedAuthors = paperAuthors
      .filter((_, i) => i !== index)
      .map((author, i) => ({ ...author, authorOrder: i + 1 }));
    setPaperAuthors(updatedAuthors);
    // Update form data and check user presence
    updateFormDataAndCheckUser(updatedAuthors);
  };

  /**
   * Update author information
   */
  const updateAuthor = (index, field, value) => {
    const normalizeName = (text) => text
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    const normalizeEmail = (text) => text.toLowerCase();

    let nextValue = value;
    if (field === 'firstName' || field === 'lastName') {
      nextValue = normalizeName(value || '');
    }
    if (field === 'email') {
      nextValue = normalizeEmail(value || '');
    }

    const updatedAuthors = [...paperAuthors];
    updatedAuthors[index][field] = nextValue;
    
    setPaperAuthors(updatedAuthors);
    // Update form data and check user presence
    updateFormDataAndCheckUser(updatedAuthors);
  };

  /**
   * Move author up in the order
   */
  const moveAuthorUp = (index) => {
    if (index > 0) {
      const updatedAuthors = [...paperAuthors];
      [updatedAuthors[index], updatedAuthors[index - 1]] = [updatedAuthors[index - 1], updatedAuthors[index]];
      updatedAuthors.forEach((author, i) => {
        author.authorOrder = i + 1;
      });
      setPaperAuthors(updatedAuthors);
      // Update form data and check user presence
      updateFormDataAndCheckUser(updatedAuthors);
    }
  };

  /**
   * Move author down in the order
   */
  const moveAuthorDown = (index) => {
    if (index < paperAuthors.length - 1) {
      const updatedAuthors = [...paperAuthors];
      [updatedAuthors[index], updatedAuthors[index + 1]] = [updatedAuthors[index + 1], updatedAuthors[index]];
      updatedAuthors.forEach((author, i) => {
        author.authorOrder = i + 1;
      });
      setPaperAuthors(updatedAuthors);
      // Update form data and check user presence
      updateFormDataAndCheckUser(updatedAuthors);
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        {/* Critical Warning Banner - Current User Missing */}
        {currentUserMissing && submissionAttempted && (
          <div className="alert alert-danger border-danger shadow-sm mb-4" role="alert" style={{borderWidth: '2px'}}>
            <div className="d-flex align-items-center">
              <i className="bi bi-shield-exclamation display-6 text-danger me-3"></i>
              <div className="flex-grow-1">
                <h4 className="alert-heading mb-2 text-danger">
                  <strong>⚠️ SUBMISSION BLOCKED</strong>
                </h4>
                <p className="mb-1 fw-bold">
                  You are not listed as an author on this paper. Paper submission requires the current user to be included in the authors list.
                </p>
                <p className="mb-0 text-danger">
                  <i className="bi bi-lock-fill me-1"></i>
                  The submit button will remain disabled until you add yourself as an author.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Warning Banner - Current User Missing (before submission attempt) */}
        {currentUserMissing && !submissionAttempted && (
          <div className="alert alert-warning border-warning mb-4" role="alert">
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle display-6 text-warning me-3"></i>
              <div className="flex-grow-1">
                <h4 className="alert-heading mb-2 text-warning">
                  <strong>⚠️ Current User Missing from Authors</strong>
                </h4>
                <p className="mb-1">
                  You are not currently listed as an author on this paper. Paper submission requires the current user to be included in the authors list.
                </p>
                <p className="mb-0 text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Please add yourself as an author or wait for the automatic process to complete.
                </p>
              </div>
            </div>
          </div>
        )}

        <h4 className="mb-4">
          <i className="bi bi-people me-2"></i>
          Author Cards
        </h4>

        {hasBlindSubmissionWarning && (
          <div className="alert alert-danger border-danger mb-4" role="alert">
            <h5 className="alert-heading">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Blind submissions are not supported
            </h5>
            <p className="mb-0">
              Please resubmit the paper with the authors added. The current extraction looks like a blind submission, so it cannot be accepted in this form.
            </p>
          </div>
        )}

        {/* Current User Missing Warning */}
        {currentUserMissing && (
          <div className={`alert ${submissionAttempted ? 'alert-danger border-danger' : 'alert-warning border-warning'} mb-4`} role="alert">
            <h5 className="alert-heading">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>{submissionAttempted ? 'SUBMISSION BLOCKED: ' : ''}Current User Missing from Authors</strong>
            </h5>
            <p className="mb-2">
              <strong>⚠️ The current logged-in user ({currentUser?.firstName} {currentUser?.lastName}) 
              is not included in the authors list.</strong>
            </p>
            {submissionAttempted ? (
              <p className="mb-3 text-danger">
                <i className="bi bi-lock-fill me-1"></i>
                <strong>Paper submission will be disabled until this is resolved.</strong> 
                The current user must be listed as an author for all paper submissions.
              </p>
            ) : (
              <p className="mb-3 text-muted">
                <i className="bi bi-info-circle me-1"></i>
                The current user must be listed as an author for all paper submissions. 
                This will be automatically handled when authors are processed, or you can add yourself manually.
              </p>
            )}
            <button
              type="button"
              className={`btn btn-sm ${submissionAttempted ? 'btn-danger' : 'btn-warning'}`}
              onClick={() => {
                const currentUserAuthor = {
                  id: Date.now(),
                  authorId: "",
                  firstName: currentUser?.firstName || "",
                  lastName: currentUser?.lastName || "",
                  email: currentUser?.email || "",
                  institution: currentUser?.primaryAffiliation?.institution || "",
                  department: currentUser?.primaryAffiliation?.department || "",
                  rankTitle: currentUser?.primaryAffiliation?.title || "",
                  country: currentUser?.primaryAffiliation?.address?.country || "",
                  isCorresponding: paperAuthors.length === 0, // First author if no others
                  authorOrder: 1, // Always put current user first
                  isCurrentUser: true // Flag to identify current user
                };
                
                // Add current user at the beginning and reorder all authors
                const updatedAuthors = [currentUserAuthor, ...paperAuthors].map((author, i) => ({
                  ...author,
                  authorOrder: i + 1
                }));
                
                setPaperAuthors(updatedAuthors);
                updateFormDataAndCheckUser(updatedAuthors);
              }}
            >
              <i className="bi bi-person-plus me-1"></i>
              Add Current User to Authors
            </button>
          </div>
        )}

        {/* Authors List */}
        <div className="mb-4">
          {paperAuthors.length === 0 ? (
            <div className="text-center py-5 border border-dashed rounded">
              <i className="bi bi-person-plus display-4 text-muted"></i>
              <h6 className="mt-3 text-muted">No authors loaded yet</h6>
              <p className="text-muted">Authors will be automatically added when you provide your information</p>
            </div>
          ) : (
            <div className="row">
              {paperAuthors.map((author, index) => {
                const isCurrentUser = isCurrentUserAuthor(author);
                const isExpanded = true; //expandedAuthors.has(author.id);
                const hasMissingInfo = !isCurrentUser && (!author.firstName || !author.lastName || !author.email || !author.institution || !author.country);
                
                return (
                <div key={author.id} className="col-12 mb-4">
                  <div className={`card ${isCurrentUser ? 'border-primary' : hasMissingInfo ? 'border-warning' : ''}`}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0 me-3">
                          <i className="bi bi-person me-2"></i>
                          Author #{author.authorOrder}
                          {author.isCorresponding && (
                            <span className="badge bg-primary ms-2">Corresponding</span>
                          )}
                          {isCurrentUser && (
                            <span className="badge bg-success ms-2">Current User</span>
                          )}
                          {hasMissingInfo && (
                            <span className="badge bg-warning text-dark ms-2">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Missing Info
                            </span>
                          )}
                        </h6>
                        
                        {/* Author name display when collapsed */}
                        {!isExpanded && (
                          <span className="text-muted">
                            {author.firstName || author.lastName ? 
                              `${author.firstName} ${author.lastName}`.trim() : 
                              'New Author'
                            }
                            {author.email && ` (${author.email})`}
                            {hasMissingInfo && (
                              <span className="text-warning ms-2">
                                <i className="bi bi-exclamation-triangle"></i>
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                      
                      <div className="btn-group" role="group">
                        {/* Expand/Collapse Button for all authors */}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            toggleAuthorExpansion(author.id);
                          }}
                          title={isExpanded ? "Collapse" : "Expand"}
                        >
                          <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                        </button>
                        
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => moveAuthorUp(index)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <i className="bi bi-arrow-up"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => moveAuthorDown(index)}
                          disabled={index === paperAuthors.length - 1}
                          title="Move down"
                        >
                          <i className="bi bi-arrow-down"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeAuthor(index)}
                          disabled={isCurrentUser}
                          title={isCurrentUser ? "Cannot remove current user" : "Remove author"}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                    
                    {/* Expandable card body */}
                    {isExpanded && (
                    <div className="card-body"
                         onBlur={(e) => {
                           // Auto-collapse when focus leaves the card body
                           // But only if no manual toggle is in progress
                           if (!manualToggleInProgress && !e.currentTarget.contains(e.relatedTarget)) {
                             if (isCurrentUser) {
                               // Current user can always be collapsed since info is auto-populated
                               safeSetTimeout(() => {
                                 if (mountedRef.current && !manualToggleInProgress) {
                                   toggleAuthorExpansion(author.id);
                                 }
                               }, 100);
                             } else {
                               // Other authors only collapse if they have required info
                               if (hasRequiredInfo(author)) {
                                 safeSetTimeout(() => {
                                   if (mountedRef.current && !manualToggleInProgress) {
                                     toggleAuthorExpansion(author.id);
                                   }
                                 }, 100);
                               }
                             }
                           }
                         }}
                         tabIndex="0"
                    >
                      {isCurrentUser && (
                        <div className="alert alert-info mb-3" role="alert">
                          <i className="bi bi-info-circle me-2"></i>
                          <strong>Current User:</strong> This is your information. You can edit these details as needed for this submission.
                        </div>
                      )}
                      
                      {hasMissingInfo && (
                        <div className="alert alert-warning mb-3" role="alert">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          <strong>Missing Required Information:</strong> Some required fields were not extracted from the document. Please fill in the highlighted fields below.
                        </div>
                      )}
                      
                      <div className="row">
                        {/* Author details */}
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            First Name <span className="text-danger">*</span>
                            {isFieldMissing(author, 'firstName') && (
                              <span className="badge bg-warning text-dark ms-1">Missing</span>
                            )}
                          </label>
                          <input
                            type="text"
                            className={`form-control ${isFieldMissing(author, 'firstName') ? 'border-warning' : ''}`}
                            value={author.firstName}
                            onChange={(e) => updateAuthor(index, 'firstName', e.target.value)}
                            placeholder="Enter first name"
                            required
                          />
                          {isFieldMissing(author, 'firstName') && (
                            <div className="form-text text-muted">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This required field is missing from AI extraction
                            </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Last Name <span className="text-danger">*</span>
                            {isFieldMissing(author, 'lastName') && (
                              <span className="badge bg-warning text-dark ms-1">Missing</span>
                            )}
                          </label>
                          <input
                            type="text"
                            className={`form-control ${isFieldMissing(author, 'lastName') ? 'border-warning' : ''}`}
                            value={author.lastName}
                            onChange={(e) => updateAuthor(index, 'lastName', e.target.value)}
                            placeholder="Enter last name"
                            required
                          />
                          {isFieldMissing(author, 'lastName') && (
                            <div className="form-text text-muted">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This required field is missing from AI extraction
                            </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Rank/Title <span className="text-danger">*</span>
                            {isFieldMissing(author, 'rankTitle') && (
                              <span className="badge bg-warning text-dark ms-1">Missing</span>
                            )}
                          </label>
                          <select
                            className="form-select"
                            value={author.rankTitle || ''}
                            required
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              updateAuthor(index, 'rankTitle', selectedValue);
                              
                              // If "Other" is deselected, clear the custom title
                              if (selectedValue !== "Other" && customRankTitles[author.id]) {
                                setCustomRankTitles(prev => {
                                  const updated = { ...prev };
                                  delete updated[author.id];
                                  return updated;
                                });
                              }
                            }}
                          >
                            <option value="">Select rank/title</option>
                            {RANK_TITLES.map((title) => (
                              <option key={title} value={title}>{title}</option>
                            ))}
                          </select>
                          
                          {/* Show custom text input when "Other" is selected */}
                          {author.rankTitle === "Other" && (
                            <div className="mt-2">
                              <input
                                type="text"
                                className="form-control"
                                required={author.rankTitle === "Other"}
                                value={customRankTitles[author.id] || ''}
                                onChange={(e) => setCustomRankTitles(prev => ({
                                  ...prev,
                                  [author.id]: e.target.value
                                }))}
                                placeholder="Please specify your rank/title"
                              />
                            </div>
                          )}
                        </div>
                        
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Email <span className="text-danger">*</span>
                            {isFieldMissing(author, 'email') && (
                              <span className="badge bg-warning text-dark ms-1">Missing</span>
                            )}
                          </label>
                          <input
                            type="email"
                            className={`form-control ${isFieldMissing(author, 'email') ? 'border-warning' : ''}`}
                            value={author.email}
                            onChange={(e) => updateAuthor(index, 'email', e.target.value)}
                            placeholder="Enter email address"
                            required
                          />
                          {isFieldMissing(author, 'email') && (
                            <div className="form-text text-muted">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This required field is missing from AI extraction
                            </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Affiliation <span className="text-danger">*</span>
                            {isFieldMissing(author, 'institution') && (
                              <span className="badge bg-warning text-dark ms-1">Missing</span>
                            )}
                          </label>
                          <input
                            type="text"
                            className={`form-control ${isFieldMissing(author, 'institution') ? 'border-warning' : ''}`}
                            value={author.institution}
                            onChange={(e) => updateAuthor(index, 'institution', e.target.value)}
                            placeholder="Enter institution"
                            required
                          />
                          {isFieldMissing(author, 'institution') && (
                            <div className="form-text text-muted">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This required field is missing from AI extraction
                            </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Department
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={author.department || ''}
                            onChange={(e) => updateAuthor(index, 'department', e.target.value)}
                            placeholder="Enter department"
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Country <span className="text-danger">*</span>
                            {isFieldMissing(author, 'country') && (
                              <span className="badge bg-warning text-dark ms-1">Missing</span>
                            )}
                          </label>
                          <select
                            className={`form-select ${isFieldMissing(author, 'country') ? 'border-warning' : ''}`}
                            value={author.country || ''}
                            onChange={(e) => updateAuthor(index, 'country', e.target.value)}
                            required
                          >
                            <option value="">Select a country...</option>
                            {countries.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                          {isFieldMissing(author, 'country') && (
                            <div className="form-text text-muted">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This required field is missing from AI extraction
                            </div>
                          )}
                        </div>

                        {/* Corresponding author checkbox */}
                        <div className="col-12">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`corresponding_${index}`}
                              checked={author.isCorresponding}
                              onChange={(e) => {
                                // If setting this as corresponding, remove corresponding from others
                                if (e.target.checked) {
                                  const updatedAuthors = paperAuthors.map((a, i) => ({
                                    ...a,
                                    isCorresponding: i === index
                                  }));
                                  setPaperAuthors(updatedAuthors);
                                  // Update form data and check user presence
                                  updateFormDataAndCheckUser(updatedAuthors);
                                } else {
                                  updateAuthor(index, 'isCorresponding', false);
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`corresponding_${index}`}>
                              Corresponding Author
                            </label>
                          </div>
                          <div className="form-text">
                            The corresponding author will receive all communications about this submission.
                          </div>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Number of Authors ({paperAuthors.length})
              {currentUserMissing && (
                <span className={`badge ${submissionAttempted ? 'bg-danger' : 'bg-warning text-dark'} ms-2`}>
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Current User Missing
                </span>
              )}
            </h5>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={addAuthor}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Author
            </button>
          </div>
        </div>

        {/* Information Box */}
        <div className="alert alert-secondary" role="alert">
          <h6 className="alert-heading">
            <i className="bi bi-lightbulb me-2"></i>
            Notes:
          </h6>
          <ul className="mb-0">
            <li>List authors in the same order as they appear in the manuscript.</li>
            <li>The signed-in user must be listed as an author.</li>
            <li>Designate the Contact Author (receives all submission communications).</li>
            <li>Author cards are minimized by default. Click the caret (^) to expand and edit.</li>
            {currentUserMissing && submissionAttempted && (
              <li className="text-danger mt-2">
                <strong><i className="bi bi-shield-exclamation me-1"></i>
                🚫 PAPER SUBMISSION IS CURRENTLY BLOCKED - Current user must be added to the authors list</strong>
              </li>
            )}
          </ul>
        </div>


        {/* Required fields notice */}
        <div className="mb-3">
          <p className="text-muted">
            <span className="text-danger">*</span> Required
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaperAuthors;
